/**
 * Staff totals for Choose one: price the default exclusive pick the same way
 * the public proposal does, and compute each alternative as if selected.
 */

import type { DesignEstimatingData, GlobalCosts, ProposalDiscount, ProposalNodeSet } from '../types';
import type { DesignRateInputs } from './proposalDesignBreakdown';
import {
  computeProposalSendSummaryEconomics,
  countGlobalCostShareEligible,
  getProposalDiscountAmount,
  type ProposalSendSummaryEconomics,
} from './proposalCalculations';
import { listExclusiveStructures, structureIsExclusive } from './proposalExclusiveGroups';

export type ExclusiveEconomicsOptions = {
  isKitProposal?: boolean;
  kitPricing?: Array<{ speciesName: string; totalCost: number }>;
  activeSpecies?: string;
  discountAmount?: number;
  designEstimating?: DesignEstimatingData | null;
  designEstimatingRates?: DesignRateInputs | null;
  proposalStatus?: string | null;
};

export type ExclusiveChoiceEconomics = {
  id: string;
  name: string;
  nodeSets: ProposalNodeSet[];
  economics: ProposalSendSummaryEconomics;
};

export type ExclusiveStaffEconomics = {
  defaultSelectedId: string | null;
  defaultNodeSets: ProposalNodeSet[];
  defaultEconomics: ProposalSendSummaryEconomics;
  choices: ExclusiveChoiceEconomics[];
  billableRange: { min: number; max: number } | null;
};

export function defaultExclusiveSelectedId(nodeSets: ProposalNodeSet[]): string | null {
  return listExclusiveStructures(nodeSets)[0]?.id ?? null;
}

/** Enabled structures with every exclusive sibling of `selectedExclusiveId` dropped. */
export function nodeSetsForExclusiveSelection<T extends ProposalNodeSet>(
  nodeSets: T[],
  selectedExclusiveId?: string | null,
): T[] {
  const enabled = nodeSets.filter(ns => ns.enabled !== false);
  const members = listExclusiveStructures(enabled);
  if (members.length === 0) return enabled;
  const pickId =
    selectedExclusiveId && members.some(m => m.id === selectedExclusiveId)
      ? selectedExclusiveId
      : members[0].id;
  return enabled.filter(ns => !structureIsExclusive(ns) || ns.id === pickId);
}

export function exclusiveChoiceBillableRange(
  choices: Array<{ economics: ProposalSendSummaryEconomics }>,
): { min: number; max: number } | null {
  if (choices.length < 2) return null;
  const bills = choices.map(c => c.economics.billableTotal);
  return { min: Math.min(...bills), max: Math.max(...bills) };
}

export function exclusivePoolShare(
  pool: ProposalNodeSet[],
  economics: ProposalSendSummaryEconomics,
): { globalAddonPerStructure: number; projectOverheadPerStructure: number } {
  const eligible = countGlobalCostShareEligible(pool);
  return {
    globalAddonPerStructure:
      economics.globalCostTotal > 0 && eligible > 0
        ? economics.globalCostTotal / eligible
        : 0,
    projectOverheadPerStructure:
      pool.length > 0 ? economics.projectOverheadAmount / pool.length : 0,
  };
}

export function computeExclusiveStaffEconomics(
  nodeSets: ProposalNodeSet[],
  profitMargin: number,
  globalCosts: GlobalCosts | null | undefined,
  options?: ExclusiveEconomicsOptions,
): ExclusiveStaffEconomics {
  const members = listExclusiveStructures(nodeSets);
  if (members.length === 0) {
    const defaultNodeSets = nodeSets.filter(ns => ns.enabled !== false);
    const defaultEconomics = computeProposalSendSummaryEconomics(
      defaultNodeSets,
      profitMargin,
      globalCosts,
      options,
    );
    return {
      defaultSelectedId: null,
      defaultNodeSets,
      defaultEconomics,
      choices: [],
      billableRange: null,
    };
  }

  const choices: ExclusiveChoiceEconomics[] = members.map(member => {
    const pool = nodeSetsForExclusiveSelection(nodeSets, member.id);
    return {
      id: member.id,
      name: member.name,
      nodeSets: pool,
      economics: computeProposalSendSummaryEconomics(pool, profitMargin, globalCosts, options),
    };
  });
  const defaultChoice = choices[0];
  return {
    defaultSelectedId: defaultChoice.id,
    defaultNodeSets: defaultChoice.nodeSets,
    defaultEconomics: defaultChoice.economics,
    choices,
    billableRange: exclusiveChoiceBillableRange(choices),
  };
}

/** Draft / list / billing projection — default Choose one pick, post-discount. */
export function staffBillableTotalFromProposal(proposal: {
  nodeSets?: ProposalNodeSet[] | null;
  profitMargin?: number;
  globalCosts?: GlobalCosts | null;
  isKitProposal?: boolean;
  kitPricing?: Array<{ speciesName: string; totalCost: number }>;
  activeSpecies?: string;
  offeredSpecies?: string[];
  discount?: ProposalDiscount | null;
  designEstimating?: DesignEstimatingData | null;
  status?: string | null;
} | null | undefined): number {
  if (!proposal?.nodeSets?.length) return 0;
  return Math.round(
    computeExclusiveStaffEconomics(
      proposal.nodeSets,
      proposal.profitMargin ?? 0,
      proposal.globalCosts,
      {
        isKitProposal: proposal.isKitProposal,
        kitPricing: proposal.kitPricing,
        activeSpecies:
          proposal.activeSpecies ||
          proposal.offeredSpecies?.[0] ||
          proposal.kitPricing?.[0]?.speciesName,
        discountAmount: getProposalDiscountAmount(proposal.discount),
        designEstimating: proposal.designEstimating,
        proposalStatus: proposal.status,
      },
    ).defaultEconomics.billableTotal,
  );
}
